import { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin } from "lucide-react";
import { useTranslation } from "react-i18next";
import devCoreLogo from "@/assets/devcore-logo.webp";
import { useLocale } from "@/i18n/LocaleProvider";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CookieSettings from "@/components/CookieSettings";

const Footer = () => {
  const { t } = useTranslation();
  const { localize } = useLocale();
  const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);




  return (
    <footer className="relative z-[1] border-t border-border bg-secondary/30">
      <div className="container py-12 md:py-16">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <Link to={localize("/#home")} className="flex items-center">
              <img src={devCoreLogo} alt="DevCore Websystems" className="h-8 w-auto" />
            </Link>
            <p className="text-sm text-muted-foreground mt-3 max-w-xs leading-relaxed">
              {t("footer.tagline")}
            </p>
            <div className="mt-5 flex items-center gap-4">
              <LanguageSwitcher />
              <a
                href="https://www.linkedin.com/company/devcore-websystems"
                target="_blank"
                rel="noopener noreferrer me"
                aria-label="DevCore on LinkedIn"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-12 sm:gap-16 lg:gap-24 text-sm text-muted-foreground">
            <div>
              <p className="font-medium text-foreground mb-4">{t("footer.solutions")}</p>
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                <Link to={localize("/pages/build-your-own-tool-instead-of-saas")} className="hover:text-foreground transition-colors">{t("footer.links.ownTool")}</Link>
                <Link to={localize("/pages/custom-crm")} className="hover:text-foreground transition-colors">{t("footer.links.crm")}</Link>
                <Link to={localize("/pages/build-an-app-without-headaches")} className="hover:text-foreground transition-colors">{t("footer.links.buildApp")}</Link>
                <Link to={localize("/pages/custom-portal-not-a-builder")} className="hover:text-foreground transition-colors">{t("footer.links.portal")}</Link>
                <Link to={localize("/pages/tools-that-dont-exist-yet")} className="hover:text-foreground transition-colors">{t("footer.links.toolsDontExist")}</Link>
                <Link to={localize("/pages/combine-your-tools-into-one-suite")} className="hover:text-foreground transition-colors">{t("footer.links.suite")}</Link>
                <Link to={localize("/pages/why-not-vibecode-it-yourself")} className="hover:text-foreground transition-colors">{t("footer.links.vibecode")}</Link>
                <Link to={localize("/pages/ai-built-into-your-software")} className="hover:text-foreground transition-colors">{t("footer.links.aiInside")}</Link>
                <Link to={localize("/pages/custom-software-development")} className="hover:text-foreground transition-colors">{t("footer.links.customSoftware")}</Link>
                <Link to={localize("/pages/software-development-agency")} className="hover:text-foreground transition-colors">{t("footer.links.agency")}</Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-12">
              <div>
                <p className="font-medium text-foreground mb-4">Navigation</p>
                <div className="flex flex-col gap-3">
                  <Link to={localize("/#services")} className="hover:text-foreground transition-colors">Build</Link>
                  <Link to={localize("/#work")} className="hover:text-foreground transition-colors">Work</Link>
                  <Link to={localize("/blueprint")} className="hover:text-foreground transition-colors">Blueprint</Link>
                  <Link to={localize("/pricing")} className="hover:text-foreground transition-colors">{t("footer.links.pricing")}</Link>
                  <Link to={localize("/integrations")} className="hover:text-foreground transition-colors">{t("integrations.footerLink")}</Link>
                  <Link to={localize("/blog")} className="hover:text-foreground transition-colors">Blog</Link>
                </div>
              </div>
              <div>
                <p className="font-medium text-foreground mb-4">{t("footer.legal")}</p>
                <div className="flex flex-col gap-3">
                  <Link to={localize("/contact")} className="hover:text-foreground transition-colors">{t("nav.contact")}</Link>
                  <Link to={localize("/imprint")} className="hover:text-foreground transition-colors">{t("footer.imprint")}</Link>
                  <Link to={localize("/privacy-policy")} className="hover:text-foreground transition-colors">{t("footer.privacy")}</Link>
                  <button
                    type="button"
                    onClick={() => setCookieSettingsOpen(true)}
                    className="text-left hover:text-foreground transition-colors"
                  >
                    {t("consent.settings.link")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-border text-xs text-muted-foreground">
          © {new Date().getFullYear()} DevCore Websystems Ltd. {t("footer.rights")}
        </div>
      </div>

      <CookieSettings open={cookieSettingsOpen} onOpenChange={setCookieSettingsOpen} />
    </footer>
  );
};

export default Footer;
